import { inject, Injectable } from '@angular/core';
import {
  WorkflowNodeExternal,
  WorkflowNodeFormWithPopulatedFieldsExternal,
} from '@workflows/types';
import { combineLatest, map, Observable } from 'rxjs';
import { WorkflowDataService, WorkflowSelectorService } from './';
import { toObservable } from '@angular/core/rxjs-interop';

/**
 * Сервис для навигации между workflow, нодами и формами
 * Использует WorkflowSelectorService для получения данных
 */
@Injectable({
  providedIn: 'root',
})
export class WorkflowNavigationService {
  private readonly dataService = inject(WorkflowDataService);
  private readonly selectorService = inject(WorkflowSelectorService);

  private readonly requestsWorkflowData$ = toObservable(
    this.dataService.$requestsWorkflowData,
  );

  /**
   * Получить URL для старта workflow
   * Возвращает последнюю посещённую форму или первую форму первого workflow
   */
  getStartWorkflowUrl$(): Observable<string> {
    return combineLatest([
      this.dataService.$requestsWorkflowDataObservable$,
      this.requestsWorkflowData$,
    ]).pipe(
      map(([entitiesData, signalData]) => {
        const wfData = signalData ?? entitiesData;

        if (!wfData) return '';

        if (this.selectorService.areWorkflowsCompleted()) {
          return '';
        }

        if (this.selectorService.areAllWorkflowNodesCompleted()) {
          // Если все ноды всех регламентов завершены, переходим на финальную страницу
          return 'workflows/final';
        }

        const workflow = wfData.requestsWorkflows[0];

        if (!workflow) return '';

        const firstNode =
          this.selectorService.findMinByOrder<WorkflowNodeExternal>(
            workflow.nodes,
          );

        if (!firstNode) return '';

        const nodeForms = wfData.workflowNodesForms
          .filter(
            (form: WorkflowNodeFormWithPopulatedFieldsExternal) =>
              form.workflowNodeId === firstNode.id,
          )
          .sort(
            (
              a: WorkflowNodeFormWithPopulatedFieldsExternal,
              b: WorkflowNodeFormWithPopulatedFieldsExternal,
            ) => a.order - b.order,
          );

        const firstForm = nodeForms[0];

        if (!firstForm) return '';

        // Если workflows не завершены и есть последняя посещённая форма
        if (
          !this.selectorService.areWorkflowsCompleted() &&
          wfData.requestWorkflowsContext.lastVisitedFormId
        ) {
          const lastVisitedForm = wfData.workflowNodesForms.find(
            (form: WorkflowNodeFormWithPopulatedFieldsExternal) =>
              form.id === wfData.requestWorkflowsContext.lastVisitedFormId,
          );

          if (lastVisitedForm) {
            // lastVisitedFormId общий для заявки: если форма есть в нескольких экземплярах
            // одной версии, продолжаем в том, где она ещё не заполнена валидно
            const candidates = wfData.requestsWorkflows.filter(workflow =>
              workflow.nodes.some(
                (n: WorkflowNodeExternal) =>
                  n.id === lastVisitedForm.workflowNodeId,
              ),
            );
            const unfinished = candidates.find(
              workflow =>
                !wfData.submissions.some(
                  sb =>
                    sb.requestWorkflowId === workflow.id &&
                    sb.workflowNodeFormId === lastVisitedForm.id &&
                    sb.isValid,
                ),
            );
            const resumeWorkflow = unfinished ?? candidates[0];

            if (resumeWorkflow) {
              return `workflows/${resumeWorkflow.id}/${lastVisitedForm.workflowNodeId}/${lastVisitedForm.id}`;
            }
          }
        }

        return `workflows/${workflow.id}/${firstNode.id}/${firstForm.id}`;
      }),
    );
  }

  /**
   * Получить следующий маршрут после sync
   * Определяет куда переходить: к следующей форме, следующему workflow или на финальную страницу
   */
  getNextRouteAfterSync(
    workflowId: string,
    currentNodeId: number,
    currentFormId: number,
    baseRoute: string,
  ): string | null {
    const wfData = this.dataService.$requestsWorkflowData();

    if (!wfData) return null;

    // Пытаемся найти следующую форму в текущем workflow
    const nextFormInWorkflow = this.selectorService.getNextFormInWorkflow(
      workflowId,
      currentNodeId,
      currentFormId,
    );

    if (nextFormInWorkflow) {
      const workflow = wfData.requestsWorkflows.find(
        wf => wf.id === workflowId,
      );

      if (!workflow) return null;

      // Ищем ноду для следующей формы
      for (const node of workflow.nodes) {
        const nodeForms = this.selectorService.getNodeForms(node.id);

        if (nodeForms.some(f => f.id === nextFormInWorkflow)) {
          return this.buildWorkflowRoute(
            baseRoute,
            workflowId,
            node.id,
            nextFormInWorkflow,
          );
        }
      }
    }

    // Если форм больше нет в текущем workflow, переходим к следующему
    const nextWorkflow = this.selectorService.getNextWorkflow(workflowId);

    if (nextWorkflow) {
      const firstNode = this.selectorService.getFirstNodeOfWorkflow(
        nextWorkflow.id,
      );

      if (!firstNode) return null;

      const firstForm = this.selectorService.getFirstFormOfNode(firstNode.id);

      if (!firstForm) return null;

      return this.buildWorkflowRoute(
        baseRoute,
        nextWorkflow.id,
        firstNode.id,
        firstForm,
      );
    }

    // Если workflows закончились, переходим на финальную страницу
    return `${baseRoute}/workflows/final`;
  }

  /**
   * Получить ссылку для возврата назад
   * Определяет куда возвращаться: к предыдущей форме, предыдущему workflow или на summary
   */
  getReturnLink(
    workflowId: string,
    nodeId: number,
    formId: number,
    baseRoute: string,
    isFinalPage: boolean,
  ): string {
    const wfData = this.dataService.$requestsWorkflowData();

    if (!wfData) return `${baseRoute}/summary`;

    // Если финальная страница - возвращаемся к последней форме последнего workflow
    if (isFinalPage) {
      const workflow = this.selectorService.getLastWorkflow();

      if (!workflow) return `${baseRoute}/summary`;

      const node = this.selectorService.getLastNodeOfWorkflow(workflow.id);

      if (!node) return `${baseRoute}/summary`;

      const lastFormId = this.selectorService.getLastFormOfNode(node.id);

      if (!lastFormId) return `${baseRoute}/summary`;

      return this.buildWorkflowRoute(
        baseRoute,
        workflow.id,
        node.id,
        lastFormId,
      );
    }

    // Если это первая форма во всем процессе - возвращаемся на summary
    if (this.selectorService.isFirstForm(workflowId, formId.toString())) {
      return `${baseRoute}/summary`;
    }

    const currentWorkflow = wfData.requestsWorkflows.find(
      w => w.id === workflowId,
    );

    if (!currentWorkflow) return `${baseRoute}/summary`;

    const currentNode = currentWorkflow.nodes.find(n => n.id === nodeId);

    if (!currentNode) return `${baseRoute}/summary`;

    // Проверяем, есть ли предыдущая форма в текущей ноде
    const currentNodeForms = this.selectorService.getNodeForms(currentNode.id);
    const currentFormIndex = currentNodeForms.findIndex(f => f.id === formId);

    // Если есть предыдущая форма в текущей ноде - переходим к ней
    if (currentFormIndex > 0) {
      const previousForm = currentNodeForms[currentFormIndex - 1];

      return this.buildWorkflowRoute(
        baseRoute,
        workflowId,
        currentNode.id,
        previousForm.id,
      );
    }

    // Если это первая форма в ноде, проверяем, является ли нода первой
    // (по отсортированному списку нод, а не по order === 1)
    const previousNode = this.selectorService.getPreviousNode(
      currentNode.id,
      currentWorkflow,
    );

    if (!previousNode) {
      // Если нода первая, ищем предыдущий workflow
      const prevWorkflow = this.selectorService.getPreviousWorkflow(workflowId);

      if (!prevWorkflow) {
        // Если нет предыдущего workflow, возвращаемся на страницу workflows
        return `${baseRoute}/workflows`;
      }

      // Переходим к последней форме последней ноды предыдущего workflow
      const lastNodeOfPrevWorkflow = this.selectorService.getLastNodeOfWorkflow(
        prevWorkflow.id,
      );

      if (!lastNodeOfPrevWorkflow) return `${baseRoute}/workflows`;

      const lastFormOfLastNode = this.selectorService.getLastFormOfNode(
        lastNodeOfPrevWorkflow.id,
      );

      if (!lastFormOfLastNode) return `${baseRoute}/workflows`;

      return this.buildWorkflowRoute(
        baseRoute,
        prevWorkflow.id,
        lastNodeOfPrevWorkflow.id,
        lastFormOfLastNode,
      );
    }

    // Если нода не первая, переходим к последней форме предыдущей ноды
    const previousNodeLastForm = this.selectorService.getLastFormOfNode(
      previousNode.id,
    );

    if (!previousNodeLastForm) return `${baseRoute}/summary`;

    return this.buildWorkflowRoute(
      baseRoute,
      workflowId,
      previousNode.id,
      previousNodeLastForm,
    );
  }

  /**
   * Построить строку маршрута для workflow
   */
  buildWorkflowRoute(
    baseRoute: string,
    workflowId: string,
    nodeId: number,
    formId: number,
  ): string {
    return `${baseRoute}/workflows/${workflowId}/${nodeId}/${formId}`;
  }
}
